import { useMemo } from 'react';
import PropTypes from 'prop-types';

import { alpha, ThemeProvider, createTheme, useTheme } from '@mui/material/styles';

import useSettings from '../hooks/useSettings';
import componentsOverride from '../theme/overrides';

/**
 * Provides a theme wrapper that applies custom primary color settings to the application's theme.
 * This component leverages the `useSettings` hook to access global color settings and dynamically adjusts
 * the theme's primary color and associated shadows based on the current settings.
 *
 * @param {Object} props - Component props.
 * @param {ReactNode} props.children - The children components that will inherit the theme context.
 * @returns {JSX.Element} A theme provider component that wraps its children with a styled theme.
 *
 */
const ThemePrimaryColor = ({ children }) => {
  const defaultTheme = useTheme();
  const { setColor } = useSettings();

  // Memoize the theme options to prevent unnecessary recalculations
  const themeOptions = useMemo(
    () => ({
      ...defaultTheme,
      palette: {
        ...defaultTheme.palette,
        ...setColor
      },
      customShadows: {
        ...defaultTheme.customShadows,
        primary: `0 8px 16px 0 ${alpha(setColor.primary.main, 0.24)}`
      }
    }),
    [setColor, defaultTheme]
  );

  // Create a new theme instance with overridden components
  const theme = createTheme(themeOptions);
  theme.components = componentsOverride(theme);

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

ThemePrimaryColor.propTypes = {
  children: PropTypes.node
};

export default ThemePrimaryColor;
